import { DepartmentDetailComponent } from './Components/department-detail/department-detail.component';
import { BrowserComponent } from './Components/browser/browser.component';
import { RoleService } from './Services/guard/role/role.service';
import { NotFoundComponent } from './Components/not-found/not-found.component';
import { OrganisationService } from './Services/guard/organisation/organisation.service';
import { ManageDepartmentService } from './Services/guard/manage-department/manage-department.service';
import { ManageRoleService } from './Services/guard/manage-role/manage-role.service';
import { PermissionService } from './Services/guard/permission/permission.service';
import { ManageExtuserService } from './Services/guard/manage-extuser/manage-extuser.service';
import { ExternalUserService } from './Services/guard/ext-user/external-user.service';
import { ManageGroupService } from './Services/guard/manage-group/manage-group.service';
import { ManageUserService } from './Services/guard/manage-user/manage-user.service';
import { UsergroupService } from './Services/guard/usergroup/usergroup.service';
import { GroupService } from './Services/guard/group/group.service';
import { UserService } from './Services/guard/user/user.service';
import { DepartmentService } from './Services/guard/department/department.service';
import { PermissionComponent } from './Components/permission/permission.component';
import { CreateOrganisationComponent } from './Components/create-organisation/create-organisation.component';
import { OrganisationsComponent } from './Components/organisations/organisations.component';
import { EditDepartmentComponent } from './Components/edit-department/edit-department.component';
import { CreateDepartmentComponent } from './Components/create-department/create-department.component';
import { DepartmentsComponent } from './Components/departments/departments.component';
import { UnauthorizedUserComponent } from './Components/unauthorized-user/unauthorized-user.component';
import { CreateRoleComponent } from './Components/create-role/create-role.component';
import { RoleUsersGroupsComponent } from './Components/role-users-groups/role-users-groups.component';
import { RolesComponent } from './Components/roles/roles.component';
import { GroupDetailComponent } from './Components/group-detail/group-detail.component';
import { UserDetailComponent } from './Components/user-detail/user-detail.component';
import { CreateExternalUserComponent } from './Components/create-external-user/create-external-user.component';
import { EditExternalUserComponent } from './Components/edit-external-user/edit-external-user.component';
import { ExternalUsersComponent } from './Components/external-users/external-users.component';
import { CreateGroupComponent } from './Components/create-group/create-group.component';
import { GroupUsersComponent } from './Components/group-users/group-users.component';
import { EditGroupComponent } from './Components/edit-group/edit-group.component';
import { ChangePinComponent } from './Components/change-pin/change-pin.component';
import { ChangePasswordComponent } from './Components/change-password/change-password.component';
import { NotificationComponent } from './Components/notification/notification.component';
import { EditUserComponent } from './Components/edit-user/edit-user.component';
import { CreateUserComponent } from './Components/create-user/create-user.component';
import { UserGroupsComponent } from './Components/user-groups/user-groups.component';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, enableProdMode } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { RouterModule, Router } from '@angular/router';
import { LocationStrategy, HashLocationStrategy } from '@angular/common';
import { UsersComponent } from './Components/users/users.component';
import { GroupsComponent } from './Components/groups/groups.component';

import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { AccordionModule } from 'primeng/accordion';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToolbarModule } from 'primeng/toolbar';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { MenubarModule } from 'primeng/menubar';
import { SplitButtonModule } from 'primeng/splitbutton';
import { TabViewModule } from 'primeng/tabview';
import { PanelModule } from 'primeng/panel';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TableModule } from 'primeng/table';
import { DropdownModule } from 'primeng/dropdown';
import { MessageModule } from 'primeng/message';
import { MessagesModule } from 'primeng/messages';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PickListModule } from 'primeng/picklist';
import { ListboxModule } from 'primeng/listbox';

import { CookieService } from 'ngx-cookie-service';
import { CalendarModule } from 'primeng/calendar';
import { ToastModule } from 'primeng/toast';
import { CardModule } from 'primeng/card';
import { KeycloakService } from 'keycloak-angular';
import { environment } from 'src/environments/environment';
import { HttpConfigInterceptor } from './Interceptors/httpconfig.interceptor';
import { detect } from 'detect-browser';
import { ServiceWorkerModule } from '@angular/service-worker';

// Import new components and services for built-in authentication
import { LoginComponent } from './Components/login/login.component';
import { AuthService } from './Services/auth/auth.service';
import { AuthGuard } from './Services/guard/auth/auth.guard';

enableProdMode();

export function HttpLoaderFactory(httpClient: HttpClient) {
   return new TranslateHttpLoader(httpClient);
}

const keycloakService = new KeycloakService();
const browser = detect();

// Function to check URL parameter for authentication type
function getAuthType(): string {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('auth') || 'keycloak'; // Default to keycloak if no parameter
}

function getRedirectUrl(): string {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('redirectUrl') || ''; // Default to '' if no parameter
}

@NgModule({
  declarations: [
    AppComponent,
    UsersComponent,
    GroupsComponent,
    UserGroupsComponent,
    CreateUserComponent,
    EditUserComponent,
    NotificationComponent,
    ChangePinComponent,
    ChangePasswordComponent,
    EditGroupComponent,
    GroupUsersComponent,
    CreateGroupComponent,
    ExternalUsersComponent,
    CreateExternalUserComponent,
    EditExternalUserComponent,
    UserDetailComponent,
    GroupDetailComponent,
    RolesComponent,
    RoleUsersGroupsComponent,
    CreateRoleComponent,
    UnauthorizedUserComponent,
    DepartmentsComponent,
    CreateDepartmentComponent,
    EditDepartmentComponent,
    OrganisationsComponent,
    CreateOrganisationComponent,
    PermissionComponent,
    NotFoundComponent,
    BrowserComponent,
    DepartmentDetailComponent,
    LoginComponent // Add the login component
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    ProgressSpinnerModule,
    AccordionModule,
    PanelModule,
    ButtonModule,
    RadioButtonModule,
    ToolbarModule,
    SplitButtonModule,
    TableModule,
    DropdownModule,
    InputTextModule,
    MessagesModule,
    MessageModule,
    ConfirmDialogModule,
    MenubarModule,
    InputTextModule,
    ProgressSpinnerModule,
    PickListModule,
    ListboxModule,
    ToastModule,
    CalendarModule,
    CardModule,
    DialogModule,
    TabViewModule,
    CheckboxModule,

    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    RouterModule.forRoot([
      { path: 'login', component: LoginComponent }, // Add login route
      { path: 'users', component: UsersComponent, canActivate: [ AuthGuard ] },
      { path: 'groups', component: GroupsComponent, canActivate: [ AuthGuard ] },
      { path: 'usergroups', component: UserGroupsComponent, canActivate: [ AuthGuard ] },
      { path: 'createuser', component: CreateUserComponent, canActivate: [ AuthGuard ] },
      { path: 'users/:id/edit', component: EditUserComponent, canActivate: [ AuthGuard ] },
      { path: 'users/:id/changepassword', component: ChangePasswordComponent, canActivate: [ AuthGuard ] },
      { path: 'users/:id/changepin', component: ChangePinComponent, canActivate: [ AuthGuard ] },
      { path: 'groups/:id/edit', component: EditGroupComponent, canActivate: [ AuthGuard ] },
      { path: 'groups/:id/groupusers', component: GroupUsersComponent, canActivate: [ AuthGuard ] },
      { path: 'creategroup', component: CreateGroupComponent, canActivate: [ AuthGuard ] },
      { path: 'externalusers', component: ExternalUsersComponent, canActivate: [ AuthGuard ] },
      { path: 'createexternaluser', component: CreateExternalUserComponent, canActivate: [ AuthGuard ] },
      { path: 'externalusers/:id', component: EditExternalUserComponent, canActivate: [ AuthGuard ] },
      { path: 'users/:id', component: UserDetailComponent, canActivate: [ AuthGuard ] },
      { path: 'groups/:id', component: GroupDetailComponent, canActivate: [ AuthGuard ] },
      { path: 'roles', component: RolesComponent, canActivate: [ AuthGuard ] },
      { path: 'permissions', component: PermissionComponent, canActivate: [ AuthGuard ] },
      { path: 'createrole', component: CreateRoleComponent, canActivate: [ AuthGuard ] },
      { path: 'managerole', component: RoleUsersGroupsComponent, canActivate: [ AuthGuard ] },
      { path: 'departments', component: DepartmentsComponent, canActivate: [ AuthGuard ] },
      { path: 'create-department', component: CreateDepartmentComponent, canActivate: [ AuthGuard ] },
      { path: 'departments/:id', component: DepartmentDetailComponent, canActivate: [ AuthGuard ] },
      { path: 'departments/:id/edit', component: EditDepartmentComponent, canActivate: [ AuthGuard ] },
      { path: 'organisations', component: OrganisationsComponent, canActivate: [ AuthGuard ] },
      { path: 'create-organisation', component: CreateOrganisationComponent, canActivate: [ AuthGuard ] },
      { path: 'unauthorizeduser', component: UnauthorizedUserComponent },
      { path: 'xxxxx', component: BrowserComponent },
      { path: '', redirectTo: '/users', pathMatch: 'full' }, // Default route
      { path: '**', redirectTo: '/login' }, // Fallback route
    ]),
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production })
  ],
  providers: [
    MessageService,
    CookieService,
    ConfirmationService,
    AuthService, // Add the auth service
    AuthGuard, // Add the auth guard
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy
    },
    {
      provide: KeycloakService,
      useValue: keycloakService
    },
    { provide: HTTP_INTERCEPTORS, useClass: HttpConfigInterceptor, multi: true }
  ],
  entryComponents: [
    AppComponent
  ]
})
export class AppModule {
  private authType: string;
  private redirectUrl: string;

  constructor() {
    this.authType = getAuthType();
    localStorage.setItem('authType', this.authType);
    
    this.redirectUrl = getRedirectUrl();
    localStorage.setItem('redirectUrl', this.redirectUrl);
    
    localStorage.setItem('oi', '');
    localStorage.setItem('ui', '');
  }

  ngDoBootstrap(app) {
    localStorage.setItem('browser', 'true');
    
    if (this.authType === 'central') {
      // Use built-in authentication
      app.bootstrap(AppComponent);
    } else {
      // Use Keycloak authentication (default)
      keycloakService
        .init({
          config: {
            url: environment.keycloakUrl + '/auth',
            realm: environment.realm,
            clientId: environment.clientid
          },
          initOptions: {
            onLoad: 'login-required',
            checkLoginIframe: false
          },
          enableBearerInterceptor: true,
          bearerPrefix: 'Bearer',
          bearerExcludedUrls: [
            '/assets',
            '/clients/public'
          ]
        })
        .then(async () => {
          localStorage.setItem('oi', '');
          localStorage.setItem('ui', '');
          app.bootstrap(AppComponent);
        })
        .catch(error => console.error('[ngDoBootstrap] init Keycloak failed', error));
    }
  }
}